import { cn } from "@/lib/utils";
import type { RagCitation } from "../types.js";

interface MessageSourcesFooterProps {
  citations: RagCitation[];
  activeCitationId?: string | null;
  onCitationClick?: (citation: RagCitation) => void;
}

function SourceCard({
  citation,
  activeCitationId,
  onCitationClick,
}: {
  citation: RagCitation;
  activeCitationId?: string | null;
  onCitationClick?: (citation: RagCitation) => void;
}) {
  const isActive = activeCitationId === citation.id;

  return (
    <button
      type="button"
      onClick={() => onCitationClick?.(citation)}
      title={`${citation.documentTitle} (${citation.relevance}%)`}
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2 py-1 text-left transition-colors",
        isActive
          ? "border-[rgba(147,51,245,0.45)] bg-[rgba(147,51,245,0.08)] ring-1 ring-[rgba(147,51,245,0.2)] dark:border-violet-400/50 dark:bg-violet-500/15"
          : "border-[rgba(15,23,42,0.08)] bg-[var(--surface-sunken)] hover:border-[rgba(147,51,245,0.28)] dark:border-white/10 dark:bg-slate-950/30 dark:hover:border-violet-400/40"
      )}
    >
      <span
        className={cn(
          "inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold leading-none",
          isActive
            ? "bg-primary text-white dark:bg-violet-500"
            : "border border-[var(--citation-border)] bg-[var(--citation-bg)] text-[#7c3aed] dark:border-transparent dark:bg-violet-600 dark:text-white"
        )}
      >
        {citation.index}
      </span>
      <span className="truncate text-[11px] font-medium text-[#576274] dark:text-slate-300">
        {citation.documentTitle}
      </span>
      <span className="shrink-0 text-[10px] font-medium text-[#576274] dark:text-slate-400">
        {citation.relevance}%
      </span>
    </button>
  );
}

export function MessageSourcesFooter({
  citations,
  activeCitationId,
  onCitationClick,
}: MessageSourcesFooterProps) {
  if (citations.length === 0) return null;

  return (
    <div className="mt-2.5 border-t border-[rgba(15,23,42,0.08)] pt-2 dark:border-white/10">
      <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-[#576274] dark:text-slate-400">
        Sources
      </div>
      <div className="flex flex-wrap gap-1.5">
        {citations.map((citation) => (
          <SourceCard
            key={citation.id}
            citation={citation}
            activeCitationId={activeCitationId}
            onCitationClick={onCitationClick}
          />
        ))}
      </div>
    </div>
  );
}
